import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { matchActivity } from './activityTracker';
import { createActivity } from '../services/activityService';

/**
 * Extended Axios Request Config with custom properties
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
    requestData: any;
  };
}

/**
 * Skip Patterns
 * Endpoints to skip activity logging
 */
const skipPatterns = [
  /^\/auth\//,
  /^\/user\/notifications/,
  /^\/user\/activities/,
  /^\/superAdmin\/activities/,
];

/**
 * Should Skip Activity Logging
 * @param url - API endpoint URL
 * @param method - HTTP method
 */
const shouldSkipActivityLogging = (url: string, method: string): boolean => {
  // Skip all GET requests (read-only)
  if (method.toUpperCase() === 'GET') return true;

  // Skip specific patterns
  return skipPatterns.some(pattern => pattern.test(url));
};

/**
 * Setup Activity Interceptor
 * Adds Axios interceptors to track user activities
 * @param axiosInstance - Axios instance
 */
export const setupActivityInterceptor = (axiosInstance: AxiosInstance): void => {
  /**
   * Request Interceptor
   * Store request data for later use in response interceptor
   */
  axiosInstance.interceptors.request.use(
    (config: ExtendedAxiosRequestConfig) => {
      const url = config.url || '';
      const method = config.method || 'GET';

      // Skip if not logging this endpoint
      if (shouldSkipActivityLogging(url, method)) {
        return config;
      }

      // Store request metadata
      config.metadata = {
        startTime: Date.now(),
        requestData: config.data,
      };

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor
   * Log activity after successful API call
   */
  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse) => {
      const config = response.config as ExtendedAxiosRequestConfig;
      const url = config.url || '';
      const method = config.method || 'GET';

      // Skip if not logging this endpoint
      if (shouldSkipActivityLogging(url, method)) {
        return response;
      }

      // Skip if no metadata (shouldn't happen)
      if (!config.metadata) {
        return response;
      }

      try {
        // Match activity type
        const activityMatch = matchActivity(
          url,
          method,
          response,
          config.metadata.requestData
        );

        if (!activityMatch) {
          // No matching activity pattern
          return response;
        }

        // Get user info from sessionStorage
        const userStr = sessionStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;

        if (!user) {
          // No user logged in, skip
          return response;
        }

        // Build activity description
        const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
        const description = `${userName} ${activityMatch.action} ${activityMatch.targetType} '${activityMatch.targetName}'`;

        // Create activity payload
        const activityPayload = {
          activityType: activityMatch.activityType,
          action: activityMatch.action,
          targetType: activityMatch.targetType,
          targetId: activityMatch.targetId,
          targetName: activityMatch.targetName,
          description,
          metadata: activityMatch.metadata,
          endpoint: url,
          method: method.toUpperCase(),
        };

        // Log activity asynchronously (don't block response)
        createActivity(activityPayload).catch((error) => {
          console.error('[ActivityInterceptor] Failed to log activity:', error);
        });
      } catch (error) {
        console.error('[ActivityInterceptor] Error processing activity:', error);
      }

      return response;
    },
    (error) => {
      // Don't log activities for failed requests
      return Promise.reject(error);
    }
  );

  console.log('[ActivityInterceptor] Activity interceptor setup complete');
};
